/**
 * The emoji offered by the picker.
 *
 * A curated list rather than a full Unicode table, and rather than a dependency.
 * The full set is ~3,800 characters plus skin-tone and ZWJ sequences; shipping it
 * costs well over 100kB of data the reader downloads to use maybe thirty of them,
 * and every emoji library also ships its own search index on top. A picker exists
 * to be faster than typing, and a grid nobody can scan is not faster.
 *
 * So: the emoji people actually send, grouped, in rough frequency order within
 * each group. Anything missing is still postable — the composer is a plain text
 * field and the system emoji keyboard works in it. This list is a shortcut, not a
 * gate, which is why it can afford to be opinionated.
 */

export interface EmojiGroup {
  readonly name: string;
  readonly emoji: readonly string[];
}

export const EMOJI_GROUPS: readonly EmojiGroup[] = [
  {
    name: "Smileys",
    emoji: [
      "😀",
      "😃",
      "😄",
      "😁",
      "😆",
      "😅",
      "🤣",
      "😂",
      "🙂",
      "🙃",
      "😉",
      "😊",
      "😇",
      "🥲",
      "🥹",
      "😍",
      "🤩",
      "😘",
      "😗",
      "😚",
      "😋",
      "😛",
      "😜",
      "🤪",
      "😝",
      "🤗",
      "🤭",
      "🤫",
      "🤔",
      "🤨",
      "😐",
      "😑",
      "😶",
      "😏",
      "😒",
      "🙄",
      "😬",
      "🤥",
      "😌",
      "😔",
      "😪",
      "😴",
      "😷",
      "🤒",
      "🤕",
      "🥴",
      "😵",
      "🤯",
      "🤠",
      "🥳",
      "😎",
      "🤓",
      "🧐",
      "😕",
      "🙁",
      "😟",
      "😮",
      "😯",
      "😲",
      "😳",
      "🥺",
      "😦",
      "😧",
      "😨",
      "😰",
      "😥",
      "😢",
      "😭",
      "😱",
      "😖",
      "😣",
      "😞",
      "😓",
      "😩",
      "😫",
      "🥱",
      "😤",
      "😡",
      "🤬",
      "💀",
    ],
  },
  {
    name: "Gestures",
    emoji: [
      "👍",
      "👎",
      "👌",
      "🤌",
      "🤏",
      "✌️",
      "🤞",
      "🫰",
      "🤟",
      "🤘",
      "👈",
      "👉",
      "👆",
      "👇",
      "☝️",
      "👋",
      "🤚",
      "🖐️",
      "✋",
      "🖖",
      "🫱",
      "🫲",
      "🫶",
      "👐",
      "🤲",
      "🤝",
      "🙏",
      "✍️",
      "💅",
      "🦾",
      "💪",
      "🦵",
      "🫀",
      "🧠",
      "👀",
      "👁️",
      "👄",
      "🫂",
      "👶",
      "🧑",
    ],
  },
  {
    name: "Hearts",
    emoji: [
      "❤️",
      "🧡",
      "💛",
      "💚",
      "💙",
      "💜",
      "🖤",
      "🤍",
      "🤎",
      "💔",
      "❣️",
      "💕",
      "💞",
      "💓",
      "💗",
      "💖",
      "💘",
      "💝",
      "🫶",
      "💯",
    ],
  },
  {
    name: "Bitcoin",
    emoji: [
      "⚡",
      "🟠",
      "🔑",
      "🗝️",
      "🔐",
      "🔒",
      "🧡",
      "📈",
      "📉",
      "💰",
      "💸",
      "🪙",
      "🏦",
      "⛏️",
      "🔗",
      "🧅",
      "🥩",
      "🫂",
      "🌱",
      "🚀",
    ],
  },
  {
    name: "Objects",
    emoji: [
      "🔥",
      "✨",
      "🎉",
      "🎊",
      "🎁",
      "🏆",
      "🥇",
      "⭐",
      "🌟",
      "💫",
      "☀️",
      "🌙",
      "☁️",
      "🌈",
      "❄️",
      "💧",
      "🌊",
      "🍀",
      "🌸",
      "🌻",
      "📱",
      "💻",
      "⌨️",
      "🖥️",
      "🖱️",
      "💾",
      "📷",
      "🎥",
      "🎧",
      "🎙️",
      "📚",
      "📝",
      "✏️",
      "📌",
      "📎",
      "🔍",
      "🔔",
      "⏰",
      "⌛",
      "🧭",
    ],
  },
  {
    name: "Food",
    emoji: [
      "☕",
      "🍵",
      "🧉",
      "🍺",
      "🍻",
      "🥂",
      "🍷",
      "🥃",
      "🧊",
      "🍹",
      "🍕",
      "🍔",
      "🌮",
      "🌯",
      "🍜",
      "🍣",
      "🥗",
      "🍎",
      "🍌",
      "🍇",
      "🍓",
      "🍒",
      "🥑",
      "🌽",
      "🥕",
      "🍞",
      "🧀",
      "🥚",
      "🍫",
      "🍪",
    ],
  },
  {
    name: "Nature",
    emoji: [
      "🐶",
      "🐱",
      "🐭",
      "🐹",
      "🐰",
      "🦊",
      "🐻",
      "🐼",
      "🐨",
      "🐯",
      "🦁",
      "🐮",
      "🐷",
      "🐸",
      "🐵",
      "🐔",
      "🐧",
      "🐦",
      "🦅",
      "🦉",
      "🐺",
      "🐗",
      "🐴",
      "🦄",
      "🐝",
      "🦋",
      "🐌",
      "🐢",
      "🐍",
      "🐙",
      "🐬",
      "🐋",
      "🦈",
      "🐊",
      "🐳",
      "🌵",
      "🌲",
      "🌳",
      "🍁",
      "🍄",
    ],
  },
  {
    name: "Symbols",
    emoji: [
      "✅",
      "❌",
      "⚠️",
      "❓",
      "❗",
      "💤",
      "🔴",
      "🟢",
      "🔵",
      "🟣",
      "⬆️",
      "⬇️",
      "➡️",
      "⬅️",
      "🔁",
      "🔂",
      "▶️",
      "⏸️",
      "⏹️",
      "🔀",
      "♻️",
      "🆗",
      "🆕",
      "🔝",
      "💬",
      "🗨️",
      "💭",
      "🔊",
      "🔇",
      "🏳️",
    ],
  },
];

/**
 * Insert `insert` into `value`, replacing the selection.
 *
 * Returns the new text and where the caret should land, so the caller can restore
 * it. Without that the caret jumps to the end of the note on every insertion,
 * which makes adding an emoji mid-sentence impossible — the one thing a picker
 * has to get right.
 */
export function insertAt(
  value: string,
  selectionStart: number,
  selectionEnd: number,
  insert: string,
): { readonly value: string; readonly caret: number } {
  // Clamp rather than trust: a stale selection from a re-render can point past
  // the end of the text, and `slice` would silently produce the wrong string.
  const start = Math.max(0, Math.min(selectionStart, value.length));
  const end = Math.max(start, Math.min(selectionEnd, value.length));
  return {
    value: value.slice(0, start) + insert + value.slice(end),
    caret: start + insert.length,
  };
}
